import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Instagram, ExternalLink, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const INSTAGRAM_URL = "https://instagram.com/sentetica.co";

const portfolioItems = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&h=600&fit=crop',
    title: 'E-commerce Campaign',
    category: 'Meta Ads',
  },
  {
    id: 2,
    image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=600&h=600&fit=crop',
    title: 'Brand Video Series',
    category: 'AI Video',
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=600&fit=crop',
    title: 'SaaS Website',
    category: 'Web Design',
  },
  {
    id: 4,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=600&fit=crop',
    title: 'Analytics Dashboard',
    category: 'Web Design',
  },
  {
    id: 5,
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=600&fit=crop',
    title: 'Social Media Campaign',
    category: 'Content',
  },
  {
    id: 6,
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=600&fit=crop',
    title: 'Brand Identity',
    category: 'Branding',
  },
  {
    id: 7,
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&h=600&fit=crop',
    title: 'Corporate Video',
    category: 'AI Video',
  },
  {
    id: 8,
    image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&h=600&fit=crop',
    title: 'Product Launch',
    category: 'Meta Ads',
  },
  {
    id: 9,
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&h=600&fit=crop',
    title: 'Growth Strategy',
    category: 'Content',
  },
];

const PortfolioSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { language } = useLanguage();

  const content = {
    en: {
      overline: 'Our Work',
      headline: 'Results That',
      headlineAccent: 'Impress',
      subheadline: 'Explore our latest projects and case studies on Instagram.',
      cta: 'View Full Portfolio on Instagram',
    },
    tr: {
      overline: 'Çalışmalarımız',
      headline: 'Etkileyen',
      headlineAccent: 'Sonuçlar',
      subheadline: 'En son projelerimizi ve vaka çalışmalarımızı Instagram\'da keşfedin.',
      cta: 'Tam Portfolyoyu Instagram\'da Görüntüle',
    },
  };

  const t = content[language] || content.en;

  return (
    <section id="portfolio" className="py-16 md:py-20 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-[#E60000] text-sm uppercase tracking-[0.2em] font-bold">{t.overline}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mt-3 tracking-tight">
            {t.headline} <span className="text-[#E60000]">{t.headlineAccent}</span>
          </h2>
          <p className="text-[#6B7280] text-base mt-3 max-w-lg mx-auto">
            {t.subheadline}
          </p>
        </motion.div>

        {/* Portfolio Grid - 3x3 */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {portfolioItems.map((item, index) => (
            <motion.a
              key={item.id}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="relative group aspect-square rounded-xl overflow-hidden cursor-pointer"
              data-testid={`portfolio-item-${index}`}
            >
              <img
                src={item.image}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />
              
              {/* Content */}
              <div className="absolute inset-0 p-4 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-all duration-300">
                <span className="text-[#E60000] text-xs uppercase tracking-wider font-bold mb-1">
                  {item.category}
                </span>
                <h3 className="font-heading text-sm md:text-base font-bold text-white">
                  {item.title}
                </h3>
              </div>

              {/* Instagram icon */}
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                <ExternalLink className="w-4 h-4 text-white" />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Instagram CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-10"
        >
          <motion.a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-7 py-3.5 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="portfolio-instagram-btn"
          >
            <Instagram size={20} />
            {t.cta}
            <ArrowRight size={16} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;
